import { useSyncExternalStore } from "react";

const LOCATION_CHANGE_EVENT = "locationchange";

function subscribeToLocation(onStoreChange: () => void) {
    window.addEventListener("popstate", onStoreChange);
    window.addEventListener(LOCATION_CHANGE_EVENT, onStoreChange);
    return () => {
        window.removeEventListener("popstate", onStoreChange);
        window.removeEventListener(LOCATION_CHANGE_EVENT, onStoreChange);
    };
}

export function useLocation() {
    const location = useSyncExternalStore(
        subscribeToLocation,
        () => window.location,
        () => window.location,
    );
    return location;
}

export function useLocationPathname() {
    const pathname = useSyncExternalStore(subscribeToLocation, getPathname, () => "/");
    return pathname;
}

function getPathname() {
    return window.location.pathname;
}

export function getCurrentLocation() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getLocationState(): unknown {
    return window.history.state ?? undefined;
}

export function useLocationState<S = unknown>() {
    return useSyncExternalStore(subscribeToLocation, getLocationState, () => undefined) as
        S | undefined;
}

export function navigate(path: string, options?: { state?: unknown }) {
    setTimeout(() => {
        window.history.pushState(options?.state ?? null, "", path);
        window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
    }, 1);
}

export function goBack() {
    setTimeout(() => {
        window.history.back();
        window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
    }, 1);
}

export function enableInternalNavigationDetector() {
    document.addEventListener("click", (event) => {
        if (event.defaultPrevented) return;
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const eventTarget = event.target;
        if (!(eventTarget instanceof Element)) return;

        const anchor = eventTarget.closest("a");
        if (!(anchor instanceof HTMLAnchorElement)) return;
        if (anchor.target !== "" && anchor.target !== "_self") return;
        if (anchor.hasAttribute("download")) return;

        const href = anchor.getAttribute("href");
        if (href === null || href === "" || href.startsWith("#")) return;

        let url: URL;
        try {
            url = new URL(href, window.location.href);
        } catch {
            return;
        }
        if (url.origin !== window.location.origin) return;

        event.preventDefault();

        const next = `${url.pathname}${url.search}${url.hash}`;
        if (next === getCurrentLocation()) return;

        window.history.pushState(null, "", next);
        window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
    });
}
